import React, { useState, useMemo, useEffect } from 'react';
import { Calculator, Package, Clock, Euro, CheckCircle2, Info, Sparkles } from 'lucide-react';
import ShareModal from './ShareModal';
import OrderConfirmationDialog from './OrderConfirmationDialog';
import Footer from './Footer';
import { ShareableConfiguration } from '../services/shareService';
import { useAuth } from '../contexts/AuthContext';
import { quoteService } from '../services/quoteService';
import { adminNotificationService } from '../services/adminNotificationService';


interface Module {
  id: string;
  name: string;
  description: string;
  hours: number;
  price: number;
  type: 'main' | 'option' | 'fixed';
  selected?: boolean;
}

const WebentwicklungKalkulator: React.FC = () => {
  const { user, userProfile } = useAuth();
  
  const hourlyRate = 60;

  // Discount state - automatically set based on user eligibility
  const [discountEnabled, setDiscountEnabled] = useState<boolean>(false);
  const [discountValue, setDiscountValue] = useState<number>(0);
  
  // Auto-apply 30% discount for eligible users (@chimperator-live.de, @imwizemann.de)
  useEffect(() => {
    if (userProfile?.isEligibleForDiscount) {
      setDiscountEnabled(true);
      setDiscountValue(30);
      console.log('✅ Auto-discount (30%) applied for:', userProfile.email);
    }
  }, [userProfile]);

  const modules: Module[] = [
    {
      id: 'web-basic',
      name: 'Web Basic',
      description: 'Wenn ihr eine einfache, moderne statische Website braucht, die zuverlässig funktioniert und gut aussieht, dann ist dieses Modul genau richtig. Wir setzen für euch eine kompakte Seite mit allen Basisfunktionen auf – schnell, übersichtlich, effektiv.',
      hours: 16,
      price: 960,
      type: 'main'
    },
    {
      id: 'web-advanced',
      name: 'Web Advanced',
      description: 'Wenn wir eine Seite bauen sollen, die nicht auf einem bestehenden Template basiert, sondern ganz frei nach euren Vorstellungen in Leadpages oder Wix.com erstellt wird, dann ist dieses Modul das Richtige. Individuell, flexibel und ganz auf eure Anforderungen abgestimmt.',
      hours: 24,
      price: 1440,
      type: 'main'
    },
    {
      id: 'content-only',
      name: 'Content only',
      description: 'Wenn wir bereits eine Website für euch erstellt haben und lediglich der Inhalt aktualisiert oder ausgetauscht werden soll, übernehmen wir das für euch. Kein neues Design, keine Strukturänderungen – nur frischer Content an den richtigen Stellen.',
      hours: 5,
      price: 300,
      type: 'main'
    },
    {
      id: 'tracking',
      name: 'Tracking & Analytics',
      description: 'Wenn ihr wissen wollt, wie Besucher:innen eure Seite nutzen, richten wir euch ein DSGVO-konformes Tracking-System ein – inklusive Meta Pixel, Matomo oder Google Analytics 4.',
      hours: 3,
      price: 180,
      type: 'option'
    },
    {
      id: 'testing',
      name: 'Testing & Qualität',
      description: 'Grundlegende Qualitätssicherung für eure Website auf verschiedenen Devices – wir testen auf verschiedenen Geräten und Browsern, um sicherzustellen, dass alles einwandfrei funktioniert.',
      hours: 2,
      price: 120,
      type: 'option'
    },
    {
      id: 'recht',
      name: 'Recht & Consent',
      description: 'Wenn ihr rechtlich auf Nummer sicher gehen wollt, integrieren wir Cookie-Consent-Tools, Datenschutzerklärungen und Impressum nach aktuellen Standards.',
      hours: 3,
      price: 180,
      type: 'option'
    },
    {
      id: 'koordination',
      name: 'Projektkoordination',
      description: 'Wir übernehmen die gesamte Abstimmung mit Dienstleistern, internen Ansprechpartner:innen und Behörden. Wir halten die Fäden in der Hand und sorgen für einen reibungslosen Ablauf.',
      hours: 6,
      price: 360,
      type: 'fixed',
      selected: true
    }
  ];

  const [selectedMain, setSelectedMain] = useState<string>('web-basic');
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set());
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const toggleOption = (optionId: string) => {
    const newOptions = new Set(selectedOptions);
    if (newOptions.has(optionId)) {
      newOptions.delete(optionId);
    } else {
      newOptions.add(optionId);
    }
    setSelectedOptions(newOptions);
  };

  const calculation = useMemo(() => {
    let totalCost = 0;
    let totalHours = 0;
    const selectedModules: Module[] = [];

    // Add main module
    const mainModule = modules.find(m => m.id === selectedMain);
    if (mainModule) {
      totalCost += mainModule.price;
      totalHours += mainModule.hours;
      selectedModules.push(mainModule);
    }

    // Add selected options
    modules.filter(m => m.type === 'option' && selectedOptions.has(m.id)).forEach(option => {
      totalCost += option.price;
      totalHours += option.hours;
      selectedModules.push(option);
    });

    // Add fixed module (Projektkoordination)
    const fixedModule = modules.find(m => m.type === 'fixed');
    if (fixedModule) {
      totalCost += fixedModule.price;
      totalHours += fixedModule.hours;
      selectedModules.push(fixedModule);
    }

    // Apply discount if enabled
    const subtotal = totalCost;
    if (discountEnabled && discountValue > 0) {
      totalCost = totalCost * (1 - discountValue / 100);
    }

    return {
      totalCost,
      totalHours,
      selectedModules,
      subtotal,
      discountAmount: discountEnabled ? subtotal - totalCost : 0
    };
  }, [selectedMain, selectedOptions, discountEnabled, discountValue]);

  const handleRequestOffer = () => {
    // Show confirmation dialog first
    if (!user || !userProfile) {
      console.error('User not authenticated');
      alert('Sie müssen angemeldet sein, um eine Bestellung aufzugeben.');
      return;
    }
    
    setShowConfirmDialog(true);
  };

  const handleConfirmOrder = async () => {
    if (!user || !userProfile) {
      console.error('User not authenticated');
      return;
    }

    // Close dialog and submit order
    setShowConfirmDialog(false);
    setIsSubmitting(true);

    try {
      const orderData = {
        clientName: userProfile.displayName,
        clientEmail: user.email || '',
        amountTotal: calculation.totalCost,
        currency: 'EUR',
        status: 'submitted' as const,
        createdByUserId: user.uid,
        createdByUserName: userProfile.displayName,
        calculatorType: 'web' as const,
        selectedModules: calculation.selectedModules.map(m => m.name),
        configuration: {
          selectedModules: calculation.selectedModules,
          discountEnabled: discountEnabled || false,
          discountValue: discountValue || 0,
          subtotal: calculation.subtotal || calculation.totalCost
        },
        totalHours: calculation.totalHours,
        submittedAt: new Date(),
        isCustomerQuote: true,
        discountPercent: discountEnabled ? discountValue : undefined,
        discountAmount: discountEnabled ? calculation.discountAmount : undefined
      };

      const quoteId = await quoteService.createQuote(orderData);
      console.log('✅ Order submitted and saved:', quoteId);
      
      // Send email notification to admins (non-blocking)
      adminNotificationService.sendNewOrderNotification({
        customerName: userProfile.displayName,
        customerEmail: user.email || '',
        orderTotal: calculation.totalCost,
        orderId: quoteId,
        modules: calculation.selectedModules.map(m => m.name),
        discountApplied: discountEnabled,
        discountPercent: discountEnabled ? discountValue : undefined,
        totalHours: calculation.totalHours
      }).catch(err => {
        console.error('Failed to send admin notification (non-blocking):', err);
      });
      
      // Redirect to confirmation page
      window.location.href = `/quote-accepted?quoteId=${quoteId}`;
    } catch (error) {
      console.error('❌ Error submitting order:', error);
      alert('Fehler beim Absenden der Bestellung. Bitte versuchen Sie es erneut.');
      setIsSubmitting(false);
    }
  };

  const handleShareConfiguration = () => {
    setIsShareModalOpen(true);
  };

  const getShareableConfiguration = (): ShareableConfiguration => {
    return {
      selectedMain,
      selectedOptions: Array.from(selectedOptions),
      totalCost: calculation.totalCost,
      totalHours: calculation.totalHours,
      timestamp: Date.now(),
      // Include discount information if enabled
      discountEnabled: discountEnabled && discountValue > 0,
      discountValue: discountEnabled ? discountValue : undefined,
      subtotal: discountEnabled && discountValue > 0 ? calculation.subtotal : undefined,
      discountAmount: discountEnabled && discountValue > 0 ? calculation.discountAmount : undefined
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col">
      <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Calculator className="h-12 w-12 text-blue-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">
              Chimperator Live intern: Webentwicklung Kalkulator
            </h1>
          </div>
          <p className="text-xl text-gray-600 mt-2">
            Stellt euch eure Website zusammen und seht sofort, was es kostet
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Configuration Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Main Module Selection */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Package className="h-6 w-6 mr-2 text-blue-600" />
                Hauptmodul wählen
              </h2>
              <div className="space-y-3">
                {modules.filter(m => m.type === 'main').map(module => (
                  <div
                    key={module.id}
                    onClick={() => setSelectedMain(module.id)}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      selectedMain === module.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                            selectedMain === module.id
                              ? 'border-blue-500 bg-blue-500'
                              : 'border-gray-300'
                          }`}>
                            {selectedMain === module.id && (
                              <div className="w-2 h-2 bg-white rounded-full" />
                            )}
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900">{module.name}</h3>
                        </div>
                        <p className="text-sm text-gray-600 mt-2 ml-8">{module.description}</p>
                      </div>
                      <div className="ml-4 text-right">
                        <p className="text-lg font-bold text-gray-900">€{module.price}</p>
                        <p className="text-sm text-gray-500">{module.hours} Stunden</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Optional Modules */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Sparkles className="h-6 w-6 mr-2 text-purple-600" />
                Zusätzliche Module
              </h2>
              <div className="space-y-3">
                {modules.filter(m => m.type === 'option').map(module => (
                  <div
                    key={module.id}
                    onClick={() => toggleOption(module.id)}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      selectedOptions.has(module.id)
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                            selectedOptions.has(module.id)
                              ? 'border-purple-500 bg-purple-500'
                              : 'border-gray-300'
                          }`}>
                            {selectedOptions.has(module.id) && (
                              <CheckCircle2 className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900">{module.name}</h3>
                        </div>
                        <p className="text-sm text-gray-600 mt-2 ml-8">{module.description}</p>
                      </div>
                      <div className="ml-4 text-right">
                        <p className="text-lg font-bold text-gray-900">+€{module.price}</p>
                        <p className="text-sm text-gray-500">{module.hours} Stunden</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Fixed Module Info */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
              <div className="flex items-start">
                <Info className="h-6 w-6 text-blue-600 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    Immer enthalten: Projektkoordination
                  </h3>
                  <p className="text-sm text-gray-600">
                    Die Projektkoordination ist in jedem Paket enthalten. Wir übernehmen die gesamte 
                    Abstimmung und sorgen für einen reibungslosen Ablauf.
                  </p>
                  <div className="mt-2 flex items-center space-x-4">
                    <span className="text-sm font-semibold text-gray-700">
                      <Euro className="inline h-4 w-4" />360
                    </span>
                    <span className="text-sm text-gray-600">
                      <Clock className="inline h-4 w-4 mr-1" />6 Stunden
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cost Summary Panel */}
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                  Kostenübersicht
                </h2>

                {/* Discount notification banner */}
                {discountEnabled && userProfile?.isEligibleForDiscount && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-green-900">
                          Partnerrabatt aktiv!
                        </p>
                        <p className="text-xs text-green-700 mt-1">
                          Als <strong>{userProfile.email?.split('@')[1]}</strong> Kunde erhalten Sie
                          automatisch <strong>30% Rabatt</strong> auf Ihr Angebot.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Selected Modules List */}
                <div className="space-y-3 mb-6">
                  {calculation.selectedModules.map(module => (
                    <div key={module.id} className="flex justify-between items-start py-2 border-b border-gray-100">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{module.name}</p>
                        <p className="text-xs text-gray-500">{module.hours} Std. × €{hourlyRate}</p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">€{module.price}</p>
                    </div>
                  ))}
                </div>

                {/* Total */}
                <div className="border-t-2 border-gray-200 pt-4">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-sm text-gray-600">Gesamtstunden:</p>
                    <p className="text-lg font-semibold text-gray-900">{calculation.totalHours} Std.</p>
                  </div>
                  
                  {/* Show subtotal if discount is enabled */}
                  {discountEnabled && discountValue > 0 && (
                    <>
                      <div className="flex justify-between items-center mb-1">
                        <p className="text-sm text-gray-600">Zwischensumme:</p>
                        <p className="text-sm font-medium text-gray-900">€{calculation.subtotal}</p>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-sm text-green-600">Rabatt ({discountValue}%):</p>
                        <p className="text-sm font-medium text-green-600">-€{calculation.discountAmount.toFixed(2)}</p>
                      </div>
                    </>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <p className="text-lg font-semibold text-gray-900">Gesamtkosten:</p>
                    <p className="text-2xl font-bold text-blue-600">€{discountEnabled && discountValue > 0 ? calculation.totalCost.toFixed(2) : calculation.totalCost}</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-6 space-y-3">
                  <button 
                    onClick={handleRequestOffer}
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Bestellung wird abgeschickt...' : 'Webseite Anfragen'}
                  </button>
                  <button 
                    onClick={handleShareConfiguration}
                    className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                  >
                    Konfiguration teilen
                  </button>
                </div>

                {/* Additional Info */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600">
                    <strong>Hinweis:</strong> Alle Preise verstehen sich als interne Verrechnungspreise 
                    auf Basis von €{hourlyRate}/Stunde. Die finale Abrechnung erfolgt nach tatsächlichem Aufwand.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
        </div>
      {/* Footer */}
      <Footer />

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        configuration={getShareableConfiguration()}
      />

      {/* Order Confirmation Dialog */}
      <OrderConfirmationDialog
        isOpen={showConfirmDialog}
        totalCost={calculation.totalCost}
        totalHours={calculation.totalHours}
        discountActive={discountEnabled}
        discountValue={discountValue}
        modules={calculation.selectedModules}
        onConfirm={handleConfirmOrder}
        onCancel={() => setShowConfirmDialog(false)}
      />
    </div>
  );
};

export default WebentwicklungKalkulator;
